import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addNotifications,
  fetchAdminNotifications,
  fetchUsers,
} from "./NotificationsAPI";

const initialState = {
  notifications: [],
  totalNotifications: 0,
  lastPage: 1, // Added to store API pagination data
  users: [],
  loading: false,
  error: null,
};

// Thunks
export const fetchAdminNotificationsAsync = createAsyncThunk(
  "notifications/fetch",
  async (page = 1, { rejectWithValue }) => {
    try {
      const data = await fetchAdminNotifications(page);
      const resp = data?.response;

      if (!resp || !resp.data) {
        throw new Error("Invalid response structure");
      }

      return {
        notifications: resp.data,
        totalNotifications: resp.total,
        lastPage: resp.last_page, // Capture last_page from API
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addNotificationsAsync = createAsyncThunk(
  "notifications/add",
  async (formData, { rejectWithValue }) => {
    try {
      // formData is passed directly
      const data = await addNotifications(formData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUsersAsync = createAsyncThunk(
  "users/fetch",
  async ({ keyword, user_type }, { rejectWithValue }) => {
    try {
      const data = await fetchUsers(keyword, user_type);
      const resp = data?.response;

      if (!resp) {
        // Handle case where response might be empty array or different structure
        return data?.response || [];
      }

      return resp;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const AdminNotificationsSlice = createSlice({
  name: "adminNotifications",
  initialState,
  reducers: {
    clearUsers: (state) => {
      state.users = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminNotificationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminNotificationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.totalNotifications = action.payload.totalNotifications;
        state.lastPage = action.payload.lastPage; // Update lastPage
        state.error = null;
      })
      .addCase(fetchAdminNotificationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(addNotificationsAsync.fulfilled, (state, action) => {
        // Note: Usually we re-fetch the list after adding, but if the API returns 
        // the new object, we can push it. However, with pagination, 
        // it's safer to just let the component trigger a re-fetch or not push 
        // if it belongs on page 1. For now, we leave it as is or do nothing.
        // state.notifications.push(action.payload); 
      })
      .addCase(addNotificationsAsync.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchUsersAsync.fulfilled, (state, action) => {
        state.users = action.payload;
      })
      .addCase(fetchUsersAsync.rejected, (state, action) => {
        state.users = [];
        state.error = action.payload || action.error.message;
      });
  },
});

// Selectors
export const selectAdminNotifications = (state) =>
  state.adminNotifications.notifications;

export const selectAdminNotificationsTotal = (state) =>
  state.adminNotifications.totalNotifications;

export const selectAdminNotificationsLastPage = (state) =>
  state.adminNotifications.lastPage;

export const selectUsers = (state) => state.adminNotifications.users;

// Actions & Reducer
export const { clearUsers } = AdminNotificationsSlice.actions;
export default AdminNotificationsSlice.reducer;